import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { ActiveToast, IndividualConfig, ToastrService } from 'ngx-toastr';
import {
  BehaviorSubject,
  Observable,
  Subject,
  Subscription,
  timer,
} from 'rxjs';
import { tap } from 'rxjs/operators';

import { ReverbService } from '../../services/reverb.service';
import {
  BellCounterEvent,
  BellCreatedEvent,
  BellNotificationChange,
  BellNotificationModel,
  BellReadAllResponse,
  NotificationDataModel,
  NotificationSeverity,
  NotificationToastEvent,
} from '../api/models/NotificationCenterModel';
import { NotificationCenterService } from '../api/notification-center.service';
import { SessionEventsService } from '../api/session-events.service';
import { AppRoutes } from '../const/AppRoutes';
import { CrudEmployeeService } from '../content/crud/crud-employee/crud-employee.service';
import { ItemUrlFinder } from '../utils/item-url-finder';
import { SentEmailPanelService } from './sent-email-panel.service';

/** Minimal surface of the shared Echo instance used here. */
interface EchoLike {
  private(name: string): any;
  leave(name: string): void;
}

/**
 * State of the bell (centre de notifications) — offered to every plan and
 * every role, no module gate.
 *
 * - `unreadCount$`: counter shown on the bell of the toolbar.
 * - Real time: private Reverb channel `notif.{tenantSlug}.user.{userUuid}` on
 *   the Echo instance SHARED with the chat (ReverbService, /broadcasting/auth).
 *   `.bell.created` → counter + discreet toast + `created$` (open panel inserts
 *   it on top); `.bell.counter` → counter (other tabs); `.toast` → flash
 *   message (ngx-toastr), never stored. Replaces the old Firebase toasts
 *   (NotificationToastService).
 * - The database is the source of truth: the counter is read again at login,
 *   when the socket reconnects, and every 10 minutes as a fallback (same
 *   pattern as SubmissionsBadgeService).
 * - Logout: leaves the channel, stops the timer, resets the counter.
 */
@Injectable({ providedIn: 'root' })
export class BellNotificationsService implements OnDestroy {
  private static readonly POLL_MS = 10 * 60 * 1000;
  private static readonly TOAST_CONFIG: Partial<IndividualConfig> = {
    progressBar: true,
    progressAnimation: 'decreasing',
    closeButton: true,
    timeOut: 8000,
  };

  private readonly _unreadCount$ = new BehaviorSubject<number>(0);
  /** Number of unread notifications (0 = no badge). */
  readonly unreadCount$: Observable<number> = this._unreadCount$.asObservable();

  /** A notification just arrived in real time (`.bell.created`). */
  readonly created$ = new Subject<BellNotificationModel>();
  /** A notification changed state from THIS tab (read, unread, archive…). */
  readonly updated$ = new Subject<BellNotificationModel>();
  /**
   * The list on screen may be stale: socket reconnected, « tout marquer comme
   * lu », or a change made in another tab (`.bell.counter`).
   */
  readonly resync$ = new Subject<void>();

  private userUuid: string | null = null;
  private channelName: string | null = null;
  private echo: EchoLike | null = null;
  private pollSub?: Subscription;
  private reconnectSub?: Subscription;

  constructor(
    private sentEmailPanel: SentEmailPanelService,
    private notificationCenterService: NotificationCenterService,
    private reverb: ReverbService,
    private employeeService: CrudEmployeeService,
    private sessionEvents: SessionEventsService,
    private toastr: ToastrService,
    private router: Router,
    private zone: NgZone,
  ) {
    this.sessionEvents.logout$.subscribe(() => this.stop());

    // Starts as soon as the authenticated user is known (login or F5).
    this.employeeService.employee.subscribe({
      next: (employee: any) => {
        const userUuid: string | undefined = employee?.user?.uuid;
        if (userUuid) {
          this.start(userUuid);
        }
      },
    });
  }

  ngOnDestroy(): void {
    this.stop();
  }

  get unreadCount(): number {
    return this._unreadCount$.value;
  }

  /** Idempotent for the same user. */
  public start(userUuid: string): void {
    if (this.userUuid === userUuid && this.pollSub) {
      return;
    }
    this.stop();
    this.userUuid = userUuid;

    this.reconnectSub = this.reverb.reconnected$.subscribe(() => {
      this.refresh();
      this.resync$.next();
    });

    // First tick = now; every tick also retries the channel if it is not
    // subscribed (socket down at login, channel authorization refused…).
    this.pollSub = timer(0, BellNotificationsService.POLL_MS).subscribe(() => {
      this.subscribeChannel();
      this.refresh();
    });
  }

  public stop(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = undefined;
    this.reconnectSub?.unsubscribe();
    this.reconnectSub = undefined;
    this.leaveChannel();
    this.userUuid = null;
    this._unreadCount$.next(0);
  }

  /** Reads the counter again from the database. Silent on error. */
  public refresh(): void {
    if (!this.userUuid) {
      return;
    }
    this.notificationCenterService.getUnreadCount().subscribe({
      next: (response) => this.setUnreadCount(response?.data?.unread_count),
      error: () => {
        /* silent: the bell is a convenience, never an error source */
      },
    });
  }

  /** Applies a counter received from the API (list, action, broadcast). */
  public setUnreadCount(count: number | null | undefined): void {
    const value = Number(count);
    if (count === null || count === undefined || isNaN(value)) {
      return;
    }
    const safe = Math.max(0, Math.floor(value));
    if (safe !== this._unreadCount$.value) {
      this._unreadCount$.next(safe);
    }
  }

  /** read | unread | archive | unarchive — the caller handles the error. */
  public change(
    notification: BellNotificationModel,
    change: BellNotificationChange,
  ): Observable<BellNotificationModel> {
    return new Observable<BellNotificationModel>((subscriber) => {
      const sub = this.notificationCenterService
        .changeState(notification.uuid, change)
        .subscribe({
          next: (response) => {
            this.setUnreadCount(response?.meta?.unread_count);
            if (response?.data) {
              this.updated$.next(response.data);
            }
            subscriber.next(response?.data);
            subscriber.complete();
          },
          error: (error) => subscriber.error(error),
        });
      return () => sub.unsubscribe();
    });
  }

  /** « Tout marquer comme lu » — the caller handles the error. */
  public readAll(): Observable<BellReadAllResponse> {
    return this.notificationCenterService.readAll().pipe(
      tap((response) => {
        this.setUnreadCount(response?.data?.unread_count ?? 0);
      }),
    );
  }

  /**
   * Opens what the notification points to (RECORD → the record,
   * EXTERNAL_URL → new tab) and marks it read. Returns false when the
   * notification has nothing to open.
   */
  public open(notification: BellNotificationModel): boolean {
    if (!notification.is_read) {
      this.change(notification, 'read').subscribe({
        error: () => {
          /* silent: opening the record matters more than the read flag */
        },
      });
    }

    if (notification.action_type === 'RECORD') {
      return this.openRecord(notification.data_model);
    }
    if (notification.action_type === 'EXTERNAL_URL') {
      return this.openExternalUrl(notification.action_url);
    }
    return false;
  }

  // ---------------------------------------------------------------------------
  // Real time
  // ---------------------------------------------------------------------------

  private subscribeChannel(): void {
    const slug = localStorage.getItem('PREFIX');
    if (!slug || !this.userUuid || this.channelName !== null) {
      return;
    }
    const echo = this.reverb.ensureConnected() as unknown as EchoLike | null;
    if (!echo) {
      return;
    }

    const name = `notif.${slug}.user.${this.userUuid}`;
    this.channelName = name;
    this.echo = echo;

    try {
      echo
        .private(name)
        .listen('.bell.created', (event: BellCreatedEvent) =>
          this.zone.run(() => this.onBellCreated(event)),
        )
        .listen('.bell.counter', (event: BellCounterEvent) =>
          this.zone.run(() => this.onBellCounter(event)),
        )
        .listen('.toast', (event: NotificationToastEvent) =>
          this.zone.run(() => this.onToast(event)),
        )
        .error(() => {
          // Authorization refused or expired: no manual token refresh here
          // (the refresh route logs the user out). The channel is retried at
          // the next fallback tick; the counter keeps coming from the API.
          this.leaveChannel();
        });
    } catch (error) {
      console.error('[BellNotifications] channel subscription failed', error);
      this.channelName = null;
      this.echo = null;
    }
  }

  private leaveChannel(): void {
    if (this.echo && this.channelName) {
      try {
        // The Echo instance kept at subscription time: never re-create a
        // connection (ensureConnected) while logging out.
        this.echo.leave(this.channelName);
      } catch (error) {
        /* the socket is already gone (ReverbService disconnects at logout) */
      }
    }
    this.channelName = null;
    this.echo = null;
  }

  private onBellCreated(event: BellCreatedEvent): void {
    if (!event) {
      return;
    }
    this.setUnreadCount(event.unread_count);

    const notification = event.notification;
    if (!notification) {
      return;
    }
    this.created$.next(notification);

    const toast = this.showToast(
      notification.severity,
      notification.title,
      notification.body,
    );
    toast?.onTap.subscribe({
      next: () => this.open(notification),
    });
  }

  private onBellCounter(event: BellCounterEvent): void {
    if (!event) {
      return;
    }
    const before = this._unreadCount$.value;
    this.setUnreadCount(event.unread_count);
    if (before !== this._unreadCount$.value) {
      this.resync$.next();
    }
  }

  private onToast(event: NotificationToastEvent): void {
    if (!event) {
      return;
    }
    const toast = this.showToast(event.severity, event.title, event.message);
    toast?.onTap.subscribe({
      next: () => {
        if (event.data_model) {
          this.openRecord(event.data_model);
        } else if (event.url) {
          this.openExternalUrl(event.url);
        }
      },
    });
  }

  private showToast(
    severity: NotificationSeverity | string | null,
    title: string | null,
    message: string | null,
  ): ActiveToast<any> | null {
    const text = message ?? '';
    const heading = title ?? '';
    if (!text && !heading) {
      return null;
    }

    const config = BellNotificationsService.TOAST_CONFIG;
    switch (severity) {
      case 'SUCCESS':
        return this.toastr.success(text, heading, config);
      case 'WARNING':
        return this.toastr.warning(text, heading, config);
      case 'ERROR':
        return this.toastr.error(text, heading, config);
      case 'INFO':
      default:
        return this.toastr.info(text, heading, config);
    }
  }

  // ---------------------------------------------------------------------------
  // Navigation
  // ---------------------------------------------------------------------------

  private openRecord(dataModel: NotificationDataModel | null): boolean {
    if (!dataModel || !dataModel.object || !dataModel.uuid) {
      return false;
    }

    // Records ItemUrlFinder does not know: a sent email opens its detail
    // panel, a web submission opens its page.
    if (dataModel.object === 'SentEmail') {
      this.sentEmailPanel.open({ uuid: dataModel.uuid });
      return true;
    }
    if (dataModel.object === 'Submission') {
      const tenantPrefix = localStorage.getItem('PREFIX') || '';
      this.router.navigateByUrl(
        tenantPrefix + '/' + AppRoutes.SUBMISSIONS + '/' + dataModel.uuid,
      );
      return true;
    }

    let url: string | null = null;
    try {
      url = ItemUrlFinder.getItemURL(dataModel);
    } catch (error) {
      url = null;
    }
    // Unknown kind of record: ItemUrlFinder answers the 404 page.
    if (!url || url === '/' + AppRoutes.ERROR_404) {
      return false;
    }

    const prefix = localStorage.getItem('PREFIX') || '';
    this.router.navigateByUrl(prefix + url);
    return true;
  }

  private openExternalUrl(url: string | null): boolean {
    if (!url || !/^https?:\/\//i.test(url)) {
      return false;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
    return true;
  }
}
